import type { Dispatch } from 'redux';
import {
    PrivateDistributionList,
    DistributionListMember,
    createActionFactoryForFeature,
    Feature,
    createThunk,
    ThunkDispatch,
    FavoritePrivateDistributionList,
    PersonType,
    contactListsLeftNavItemId,
} from '../../../shared';
import { savePrivateDL, editPrivateDL } from '../data/createEditPrivateDLDataActions';
import { getAppState, getCurrentWritableContactFolderId } from '../../../appstate';
import { getPersonasInFolder, editMatchingFavoritePersona } from '../../../hub';

const createActionFactory = createActionFactoryForFeature(Feature.PrivateDistributionList);

export const createPrivateDLSaveClickedAction = createActionFactory(
    'CreatePrivateDLSaveClickedAction'
);
export const editPrivateDLSaveClickedAction = createActionFactory('EditPrivateDLSaveClickedAction');
export const editOrCreatePrivateDLCancelClickedAction = createActionFactory(
    'EditOrCreatePrivateDLCancelClickedAction'
);
export const editOrCreatePrivateDLTryAgainClickedAction = createActionFactory(
    'EditOrCreatePrivateDLTryAgainClickedAction'
);

export const privateDLCreatedAction = createActionFactory<{
    hubPersonaId?: string;
    folderId: string;
    isDefaultFolder: boolean;
}>('PrivateDLCreatedAction');

export const privateDLEditedAction = createActionFactory<{ hubPersonaId: string }>(
    'PrivateDLEditedAction'
);

export const onEditOrCreatePrivateDLCancelClicked = (dispatch: Dispatch) => () =>
    dispatch(editOrCreatePrivateDLCancelClickedAction());

export const onEditPrivateDLSaveClicked = (dispatch: ThunkDispatch) => (
    currentList: PrivateDistributionList,
    newName: string,
    newNotes: string,
    newMembers: DistributionListMember[],
    hasChange: boolean,
    parentFolderId: string
) => {
    dispatch(editPrivateDLSaveClickedAction());

    if (hasChange) {
        editPrivateDL(dispatch)(currentList, newName, newNotes, newMembers, parentFolderId)
            .then(() => {
                dispatch(privateDLSaved(currentList.hubPersonaId, false));

                const updatedDlInfo: Partial<FavoritePrivateDistributionList> = {
                    type: PersonType.FavoritePrivateDistributionList,
                    info: {
                        itemId: currentList.itemId,
                        displayName: newName,
                        members: newMembers,
                    },
                };
                dispatch(editMatchingFavoritePersona(currentList, updatedDlInfo));
            })
            .catch(_error => {
                // Nothing to do here, the error is already handled in editPrivateDL via the privateDLEditCompletedAction.error action
            });
    } else {
        dispatch(privateDLSaved(currentList.hubPersonaId, false));
    }
};

export const onCreatePrivateDLSaveClicked = (dispatch: ThunkDispatch) => (
    newName: string,
    newNotes: string,
    newMembers: DistributionListMember[],
    parentFolderId: string
) => {
    dispatch(createPrivateDLSaveClickedAction());
    savePrivateDL(dispatch)(newName, newNotes, newMembers, parentFolderId)
        .then(newPDLHubPersonaId => {
            dispatch(privateDLSaved(newPDLHubPersonaId, true));
        })
        .catch(_error => {
            // Nothing to do here, the error is already handled in savePrivateDL via the privateDLCreateCompletedAction.error action
        });
};

export const onEditOrCreatePrivateDLTryAgainClicked = (dispatch: Dispatch) => () => {
    dispatch(editOrCreatePrivateDLTryAgainClickedAction());
};

export const privateDLSaved = (hubPersonaId: string, isNewPrivateDL: boolean) =>
    createThunk('PrivateDLSaved', (dispatch: ThunkDispatch, getState: () => {}): void => {
        if (isNewPrivateDL) {
            const appState = getAppState(getState());
            const isCAPIv3Enabled = appState.view.layoutState.isCAPIv3Enabled;
            const folderId = isCAPIv3Enabled
                ? contactListsLeftNavItemId
                : getCurrentWritableContactFolderId(appState);
            const defaultContactFolder = appState.data.contactFoldersState.defaultContactFolder;
            const defaultContactFolderId = defaultContactFolder?.id;

            dispatch(
                privateDLCreatedAction({
                    hubPersonaId,
                    folderId: folderId,
                    isDefaultFolder: defaultContactFolderId === folderId,
                })
            );

            // Always re-fetch personas in the folder where the CL was saved as we will be navigating to this folder
            if (isCAPIv3Enabled) {
                /* Without user folders, we will redirect to "Your/All contact lists" view which is populated with
                the contact lists in the root contact folder */
                getPersonasInFolder(dispatch)(
                    appState.data.contactFoldersState.rootContactFolder.id
                );
            } else {
                getPersonasInFolder(dispatch)(folderId);
            }
        } else {
            dispatch(privateDLEditedAction({ hubPersonaId }));
        }
    });
export type { Action } from '../../../shared';
